import { useI18nContext } from '@lemonade-hq/nuiza';
import { devWarn } from '@lemonade-hq/ts-helpers';
import { useNavContext } from '@lemonade-hq/uniclient-app';
import type { UniclientAction } from '@lemonade-hq/uniclient-chat';
import { next, openUrl } from '@lemonade-hq/uniclient-chat';
import { Flex, Layout, LinkButton, spacing, Text, useMobileKeyboardHeight } from '@lemonade-hq/uniclient-theme';
import type { Variants } from 'motion/react';
import { AnimatePresence, motion } from 'motion/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { freeTextAttribute } from '../../../persistence/useConversationRouterPersistence';
import { DEFAULT_COLUMNS } from '../../utils/themeColumns';
import { BOTTOM_PADDING } from '../../WizardActions';
import { WIZARD_LEFT_SIDE_COLUMNS, WIZARD_RIGHT_SIDE_COLUMNS } from '../../WizardFrame.css';
import { WizardLayoutContainer } from '../../WizardLayoutContainer';
import { LAYOUT_PADDING_FROM_THE_SIDE } from '../../WizardMain.css';
import { WizardSideGrid } from '../../WizardSideGrid';
import { FreeTextInput } from './FreeTextInput';
import * as styles from './FreeTextInputController.css';

const freeTextInputAnimationVariants: Variants = {
  hidden: {
    y: '100%',
    opacity: 0,
    transition: {
      delay: 0.1,
      opacity: { duration: 0.2, ease: 'easeIn' },
      y: { duration: 0.3, ease: 'easeInOut' },
    },
  },
  visible: {
    y: '0%',
    opacity: 1,
    transition: {
      opacity: { duration: 0.1, ease: 'easeOut' },
      y: { duration: 0.3, ease: 'easeInOut' },
    },
  },
};

export interface FreeTextInputControllerProps {
  readonly placeholder?: string;
  readonly isVisible?: boolean;
  readonly isLoading?: boolean;
  readonly currentStepAnswers: Record<string, unknown>;
  readonly updateAnswers: (values: Record<string, unknown>) => void;
  readonly performAction: (action: UniclientAction) => Promise<unknown>;
  readonly currentStepAction?: UniclientAction;
  readonly onClickOutside?: () => void;
}

export const FreeTextInputController: React.FC<FreeTextInputControllerProps> = ({
  placeholder = 'Ask anything...',
  isVisible = true,
  isLoading = false,
  currentStepAnswers,
  updateAnswers,
  performAction,
  onClickOutside,
}) => {
  const { t } = useI18nContext();
  const { nav } = useNavContext();
  // Use empty string only if there's actually a value, otherwise undefined
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const inputValue = (currentStepAnswers?.[freeTextAttribute] as string) || '';
  const [isNativeKeyboardOpen, setIsNativeKeyboardOpen] = useState(false);
  const [wasDisclaimerShownOnce, setWasDisclaimerShownOnce] = useState(false);

  const isFirstRenderRef = useRef(false);

  const handleKeyboardChange = useCallback((isOpen: boolean) => {
    if (isOpen) isFirstRenderRef.current = true;
    else if (isFirstRenderRef.current) setWasDisclaimerShownOnce(true);

    setIsNativeKeyboardOpen(isOpen);
  }, []);

  useMobileKeyboardHeight({
    onChange: handleKeyboardChange,
  });
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isVisible || !onClickOutside || isNativeKeyboardOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;

      if (containerRef.current && !containerRef.current.contains(target)) {
        const targetElement = target as Element;
        const isHistoryClick = Boolean(
          // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
          targetElement.closest('[class*="history"]') || targetElement.closest('[class*="historyOverlay"]'),
        );

        if (!isHistoryClick) {
          onClickOutside();
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClickOutside, isNativeKeyboardOpen]);

  const handleOnChange = useCallback(
    async (newValue: string) => {
      const valueToStore = newValue.trim() === '' ? '' : newValue;
      updateAnswers({
        [freeTextAttribute]: valueToStore,
      });
    },
    [updateAnswers],
  );

  const handleOnSubmit = useCallback(async () => {
    try {
      await performAction(next());
    } catch {
      devWarn('Error performing action in FreeTextInputController');
    }
  }, [performAction]);

  return (
    <AnimatePresence>
      {isVisible && (
        <Flex
          animate="visible"
          as={motion.div}
          backgroundColor="$backgroundPrimary"
          bottom={0}
          exit="hidden"
          gap={spacing.s16}
          initial="hidden"
          pointerEvents="auto"
          position="fixed"
          px={LAYOUT_PADDING_FROM_THE_SIDE}
          ref={containerRef}
          variants={freeTextInputAnimationVariants}
          zIndex={1000}
        >
          <WizardSideGrid columns={WIZARD_LEFT_SIDE_COLUMNS} />
          <WizardLayoutContainer className={styles.maxWidthContainer} columns={DEFAULT_COLUMNS}>
            <Layout
              gridColumn={{
                sm: `span ${DEFAULT_COLUMNS.sm}`,
                md: `span ${DEFAULT_COLUMNS.md}`,
                lg: `2 / span ${DEFAULT_COLUMNS.lg}`,
              }}
            >
              <Flex
                alignItems={{ sm: 'left', lg: 'start' }}
                bottom={0}
                className={styles.positionContainer}
                flexDirection="column"
                justifyContent={{
                  sm: 'flex-end',
                  lg: 'flex-start',
                }}
                pb={{
                  sm: `${BOTTOM_PADDING}px`,
                  md: spacing.s40,
                }}
                width={{
                  sm: '100%',
                  lg: 'auto',
                }}
              >
                <Layout className={styles.freeTextInputOverlayContainer} position="absolute" />

                <FreeTextInput
                  isLoading={isLoading}
                  onChange={handleOnChange}
                  onSubmit={handleOnSubmit}
                  placeholder={placeholder}
                  value={inputValue}
                />

                {!(isNativeKeyboardOpen && wasDisclaimerShownOnce) && (
                  <Flex justifyContent="center" pt={spacing.s16} width="100%">
                    <Text color="$textSecondary" textAlign="center" type="bodySmall">
                      {t('chat.disclaimer', undefined, {
                        interpolations: {
                          privacyPolicyLink: text => (
                            <LinkButton
                              onClick={() => {
                                void performAction(openUrl({ url: nav.privacyPolicyUrl }));
                              }}
                              variant="inherit"
                            >
                              {text}
                            </LinkButton>
                          ),
                        },
                      })}
                    </Text>
                  </Flex>
                )}
              </Flex>
            </Layout>
          </WizardLayoutContainer>
          <WizardSideGrid columns={WIZARD_RIGHT_SIDE_COLUMNS} />
        </Flex>
      )}
    </AnimatePresence>
  );
};
